/**
 * AppointmentHoverCard.js
 * Cartão que aparece ao passar o mouse sobre um card da agenda (desktop):
 * resume o agendamento (status, horário, paciente, procedimento, profissional,
 * sala, valor, etapas e observações) sem precisar abrir o painel. Some ao clicar,
 * para não ficar por cima do painel de detalhes.
 */

import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import FirstVisitIcon from '@mui/icons-material/AutoAwesome';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { DataProviderInstance } from '../../../api/DataProvider';
import AppointmentsState from '../../../consts/AppointmentsState';
import { getStatusColors } from '../../../consts/AppointmentStatusColors';
import useAppointmentSteps from '../../appointment-steps/useAppointmentSteps';
import StepsProgressRing from '../../appointment-steps/components/StepsProgressRing';
import { stepProgressFor, readyLabelFor, appointmentColorsFor } from '../../appointment-steps/appointmentSteps';
import CalendarConfig from '../../../consts/CalendarConfig';
import { formatPhone } from '../../../utils/phoneUtils';
import { formatPrice, procedureLabels, appointmentProcedureIds } from '../v2/utils/appointmentUtils';
import { isOnlineAppointment } from '../v2/utils/attendanceMode';
import { isInsuranceAppointment, coverageLabel } from '../v2/utils/billingType';
import { insuranceAlertsOf } from '../v2/utils/insuranceAlerts';
import InsuranceAlertList from '../v2/components/InsuranceAlerts/InsuranceAlertList';
import styles from './AppointmentHoverCard.module.css';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const pad = (n) => String(n).padStart(2, '0');

// Reserva: o bloqueio guarda "Reservado: <telefone>"; devolve só o telefone
export function getReservationPhone(label) {
  return String(label || '').replace(/^Reservado:?\s*/, '').trim();
}

function durationToMinutes(duration) {
  if (!duration) return 0;
  if (!String(duration).includes(':')) return Number(duration) || 0;
  const [h, m] = String(duration).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

// Horário em duas partes: a hora em destaque ("11:00 – 11:30") e o dia com a
// duração embaixo ("Ter, 3 nov · 30 min")
function formatWhen(date, duration) {
  const start = new Date(date);
  if (isNaN(start)) return null;
  const day = `${WEEKDAYS[start.getDay()]}, ${start.getDate()} ${MONTHS[start.getMonth()]}`;
  const startTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
  const minutes = durationToMinutes(duration);
  if (!minutes) return { time: startTime, day };
  const end = new Date(start.getTime() + minutes * 60000);
  return { time: `${startTime} – ${pad(end.getHours())}:${pad(end.getMinutes())}`, day: `${day} · ${minutes} min` };
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>
        {Icon && <Icon sx={{ fontSize: 14 }} />}
        {label}
      </span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

/** Etapas do status atual (só quando a empresa cadastrou etapas para ele) */
function StepsBlock({ progress }) {
  if (!progress) return null;
  const useRequired = progress.totalRequired > 0;
  const done = useRequired ? progress.doneRequired : progress.done;
  const total = useRequired ? progress.totalRequired : progress.total;
  return (
    <div className={styles.steps}>
      <div className={styles.stepsHeader}>
        <span className={styles.rowLabel}>Etapas</span>
        {progress.ready ? (
          <span className={styles.stepsReady}>
            <CheckCircleRoundedIcon sx={{ fontSize: 13 }} />
            {readyLabelFor(progress.status)}
          </span>
        ) : (
          <span className={styles.stepsCounter}>
            <StepsProgressRing done={done} total={total} size={13} />
            {done} de {total}{useRequired ? ' obrigatórias' : ''}
          </span>
        )}
      </div>
      <div className={styles.stepList}>
        {progress.steps.map((step) => {
          const isDone = Boolean(progress.checks[step.Id]);
          const Icon = isDone ? CheckCircleRoundedIcon : RadioButtonUncheckedRoundedIcon;
          return (
            <span key={step.Id} className={`${styles.step} ${isDone ? styles.stepDone : ''}`}>
              <Icon sx={{ fontSize: 15 }} />
              <span className={styles.stepName}>{step.Name}</span>
              {!step.IsRequired && <span className={styles.stepOptional}>opcional</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function HoverCardContent({ appointment }) {
  const { CompanyConfig, GetPatientById, GetResourceById } = DataProviderInstance();
  const appointmentSteps = useAppointmentSteps();

  const isBlocked = appointment.status === AppointmentsState.Blocked;
  const isReservation = isBlocked && Boolean(appointment.reservationId);
  const colors = isBlocked ? getStatusColors(appointment.status) : appointmentColorsFor(appointment, appointmentSteps).colors;
  const stepProgress = isBlocked ? null : stepProgressFor(appointment, appointmentSteps);
  const statusLabel = isReservation ? 'Reservado' : AppointmentsState.GetLabel(appointment.status);

  const patient = !isBlocked && appointment.patient ? GetPatientById(appointment.patient) : null;
  // Todos os procedimentos do agendamento ("Axila + Virilha + Pernas")
  const procedureLabel = procedureLabels(appointment, CompanyConfig?.Procedures);
  const hasManyProcedures = appointmentProcedureIds(appointment).length > 1;
  const resource = appointment.resource ? GetResourceById(appointment.resource) : null;
  const room = appointment.roomId
    ? (CompanyConfig?.Rooms || []).find((r) => String(r.Id) === String(appointment.roomId))
    : null;
  const isOnline = isOnlineAppointment(appointment);
  // Cobertura só para quem trabalha com convênio (ou agendamento já por convênio)
  const showCoverage = isInsuranceAppointment(appointment)
    || (CompanyConfig?.InsuranceProviders || []).length > 0;

  let title = patient?.name || '';
  if (isReservation) title = getReservationPhone(appointment.patient) || 'Reservado';
  else if (isBlocked) title = appointment.patient || 'Bloqueado';
  const age = patient ? CalendarConfig.GetShortAge(patient.birthday) : null;
  const phone = patient?.phone ? formatPhone(patient.phone) : null;
  const isPaid = appointment.invoiceId && appointment.invoiceId !== '0' && appointment.invoiceId !== 0;
  const price = Number(appointment.price) > 0 ? formatPrice(Number(appointment.price)) : null;

  let blockScope = null;
  if (isBlocked) {
    blockScope = resource?.Label || 'Agenda inteira';
  }

  const when = formatWhen(appointment.date, appointment.duration);
  const showFirstVisit = appointment.isFirstVisit && !isBlocked;
  // Reserva usa o âmbar próprio; os demais, a cor do status (ou a escolhida pela empresa)
  const tone = isReservation
    ? { '--hc-accent': '#D97706', '--hc-tint': '#FFFBEB', '--hc-text': '#B45309' }
    : { '--hc-accent': colors.legendBorder, '--hc-tint': colors.legendBg, '--hc-text': colors.textColor };

  return (
    <div className={styles.content} style={tone}>
      <div className={styles.hero}>
        <div className={styles.heroTop}>
          <span className={`${styles.status} ${isReservation ? styles.statusReserved : ''}`}>
            <span className={`${styles.statusDot} ${isReservation ? styles.statusDotReserved : ''}`} />
            {statusLabel}
          </span>
          {isPaid && <span className={styles.paidPill}>Pago</span>}
        </div>

        {title && (
          <div className={styles.titleRow}>
            <span className={styles.title}>{title}</span>
            {age && <span className={styles.age}>{age}</span>}
          </div>
        )}

        {(phone || showFirstVisit) && (
          <div className={styles.metaRow}>
            {phone && (
              <span className={styles.phone}>
                <PhoneOutlinedIcon sx={{ fontSize: 14 }} /> {phone}
              </span>
            )}
            {showFirstVisit && (
              <span className={styles.firstVisit}>
                <FirstVisitIcon sx={{ fontSize: 12 }} /> Primeira vez
              </span>
            )}
          </div>
        )}

        {isReservation && (
          <span className={styles.reservedPill}>Aguardando o paciente preencher o cadastro</span>
        )}
      </div>

      {when && (
        <div className={styles.when}>
          <span className={styles.whenIcon}><AccessTimeRoundedIcon sx={{ fontSize: 17 }} /></span>
          <div className={styles.whenText}>
            <span className={styles.whenTime}>{when.time}</span>
            <span className={styles.whenDay}>{when.day}</span>
          </div>
        </div>
      )}

      <div className={styles.rows}>
        {isBlocked ? (
          <>
            <Row icon={isReservation ? PersonOutlineOutlinedIcon : LockOutlinedIcon} label={isReservation ? 'Agenda' : 'Bloqueia'} value={blockScope} />
            {isReservation && <Row icon={MeetingRoomOutlinedIcon} label="Sala" value={room?.Label || appointment.roomName} />}
          </>
        ) : (
          <>
            <Row icon={MedicalServicesOutlinedIcon} label={hasManyProcedures ? 'Procedimentos' : 'Procedimento'} value={procedureLabel || null} />
            <Row icon={PersonOutlineOutlinedIcon} label="Agenda" value={resource?.Label} />
            {isOnline
              ? <Row icon={VideocamOutlinedIcon} label="Modalidade" value={appointment.meeting_url ? 'Online · link informado' : 'Online'} />
              : <Row icon={MeetingRoomOutlinedIcon} label="Sala" value={room?.Label || appointment.roomName} />}
            {showCoverage && <Row icon={HealthAndSafetyOutlinedIcon} label="Cobertura" value={coverageLabel(appointment)} />}
            <Row icon={PaidOutlinedIcon} label="Valor" value={price} />
          </>
        )}
      </div>

      {!isBlocked && insuranceAlertsOf(appointment).length > 0 && (
        <div className={styles.insuranceAlerts}>
          <InsuranceAlertList alerts={insuranceAlertsOf(appointment)} dense />
        </div>
      )}

      <StepsBlock progress={stepProgress} />

      {appointment.notes && <p className={styles.notes}>{appointment.notes}</p>}

      <span className={styles.hint}>Clique para abrir <ArrowForwardRoundedIcon sx={{ fontSize: 12 }} /></span>
    </div>
  );
}

function AppointmentHoverCard({ appointment, children }) {
  const [open, setOpen] = useState(false);

  const child = React.Children.only(children);
  const handleClick = (event) => {
    setOpen(false);
    if (child.props.onClick) child.props.onClick(event);
  };

  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={<HoverCardContent appointment={appointment} />}
      placement="right-start"
      enterDelay={400}
      enterNextDelay={250}
      disableInteractive
      disableTouchListener
      componentsProps={{ tooltip: { className: styles.tip } }}
      // Card largo (visão Diário): sem espaço dos lados, abre embaixo ou em cima
      PopperProps={{ modifiers: [{ name: 'flip', options: { fallbackPlacements: ['left-start', 'bottom', 'top'] } }] }}
    >
      {React.cloneElement(child, { onClick: handleClick })}
    </Tooltip>
  );
}

export default AppointmentHoverCard;
